import { WifiOff, Wifi } from "lucide-react";
import { useNetworkStatus } from "../hooks/useNetworkStatus";
import { cn } from "../utils/cn";
import type { NetworkStatus } from "../hooks/useNetworkStatus";

interface BannerConfig {
  message: string;
  variant: string;
  Icon: typeof WifiOff | typeof Wifi;
}

const BANNER_CONFIG: Record<Exclude<NetworkStatus, "online">, BannerConfig> = {
  offline: {
    message: "You're offline — Changes will sync when reconnected.",
    variant: "network-banner--offline",
    Icon: WifiOff,
  },
  recovered: {
    message: "Back online!",
    variant: "network-banner--recovered",
    Icon: Wifi,
  },
};

/**
 * Persistent banner displayed at the top of the viewport when the
 * network connection is lost. Shows recovery feedback briefly
 * when connectivity returns.
 *
 * - Offline: Red banner (persistent until connection returns)
 * - Recovered: Green banner (auto-dismisses after 2s)
 * - Online: Nothing rendered
 *
 * Uses z-index 90 (above all other UI elements).
 */
export function NetworkStatusBanner() {
  const { status } = useNetworkStatus();

  if (status === "online") {
    return null;
  }

  const config = BANNER_CONFIG[status];
  const { Icon } = config;

  return (
    <div
      className={cn("network-banner", config.variant)}
      id="network-status-banner"
      role="alert"
    >
      <Icon className="network-banner-icon" aria-hidden="true" />
      <span>{config.message}</span>
    </div>
  );
}
